import { useState } from "react";

/**
 * Tiny presentational primitives used inside the ProgramDetails
 * panel. Kept together here because they only have meaning inside
 * the Impact-style "Program Terms" layout — no other page uses them.
 *
 *   • KV                       — label / value row with bottom border.
 *   • Section                  — titled section with capitalised header.
 *   • PayoutGroupsTable        — 3-col rank / condition / payout.
 *   • PayoutRestrictionsTable  — 2-col condition / payout-with-fallback.
 *   • ContractImagesGallery    — admin-uploaded screenshots with PDF
 *                                fallback + click-to-expand lightbox.
 */

export function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="py-2 grid grid-cols-[180px_1fr] gap-4 border-b border-slate-100 last:border-0">
      <div className="text-[12px] font-semibold text-slate-500">{label}</div>
      <div className="text-[13px] text-slate-800">{value}</div>
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="text-[13px] font-bold tracking-[0.18em] uppercase text-slate-900 pb-2 mb-3 border-b border-slate-300">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function PayoutGroupsTable({ rows }) {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return null;
  return (
    <div className="overflow-hidden border border-slate-200 rounded-md bg-white">
      <div className="grid grid-cols-[60px_1fr_200px] gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
        <span>Rank</span>
        <span>Condition</span>
        <span className="text-right">Payout</span>
      </div>
      {arr.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[60px_1fr_200px] gap-4 px-4 py-2.5 border-b border-slate-100 last:border-0 text-[13px]"
        >
          <span className="font-mono text-slate-400">{r.rank === "" || r.rank == null ? i + 1 : r.rank}</span>
          <span className="leading-relaxed text-slate-700">{r.condition || "—"}</span>
          <span className="text-right font-mono text-[#002FA7] font-medium">{r.payout || "—"}</span>
        </div>
      ))}
    </div>
  );
}

export function PayoutRestrictionsTable({ rows }) {
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return null;
  return (
    <div className="overflow-hidden border border-slate-200 rounded-md bg-white">
      <div className="grid grid-cols-[1fr_140px] gap-4 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">
        <span>Condition</span>
        <span className="text-right">Payout</span>
      </div>
      {arr.map((r, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_140px] gap-4 px-4 py-2.5 border-b border-slate-100 last:border-0 text-[13px]"
        >
          <span className="leading-relaxed text-slate-700">{r.condition || "—"}</span>
          <span className="text-right text-slate-500 italic">{r.payout || "none"}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders admin-uploaded contract screenshots as clickable thumbnails.
 * PDFs are shown as a rose tile linking to the PDF in a new tab;
 * images open in a full-screen lightbox so the publisher can read
 * the source-network table without leaving the brand row.
 */
export function ContractImagesGallery({ images }) {
  const [active, setActive] = useState(null);
  const arr = Array.isArray(images) ? images : [];
  if (arr.length === 0) return null;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {arr.map((img) => {
          const isPdf = img.content_type === "application/pdf";
          return isPdf ? (
            <a
              key={img.id}
              href={img.data_uri}
              target="_blank"
              rel="noreferrer"
              className="border border-slate-200 rounded-md overflow-hidden bg-rose-50 hover:bg-rose-100 transition-colors flex flex-col items-center justify-center h-28"
            >
              <span className="text-[11px] font-bold text-rose-700 tracking-wider">PDF</span>
              <span className="text-[10px] text-rose-600 mt-1 px-2 truncate max-w-full">{img.name}</span>
            </a>
          ) : (
            <button
              key={img.id}
              type="button"
              onClick={() => setActive(img)}
              className="border border-slate-200 rounded-md overflow-hidden bg-white hover:border-[#002FA7] transition-colors group"
              data-testid={`pub-contract-img-${img.id}`}
            >
              <div className="h-28 bg-slate-100 overflow-hidden">
                <img
                  src={img.data_uri}
                  alt={img.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              </div>
              <div className="px-2 py-1 text-[10px] text-slate-600 truncate text-left" title={img.name}>
                {img.name}
              </div>
            </button>
          );
        })}
      </div>
      {active && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setActive(null)}
          data-testid="pub-contract-img-lightbox"
        >
          <img
            src={active.data_uri}
            alt={active.name}
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl"
          />
        </div>
      )}
    </>
  );
}
